import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

/**
 * Public reusable ad slot. Pages should NOT hardcode ad content — instead:
 *   <AdSlot slotCode="home_schedule_top" />
 *
 * Layout policy (all slots share):
 *   - Up to 5 items in a single row, no wrap, no horizontal scroll.
 *   - Column count adapts to actual ads returned (1..5), heights are equal.
 *   - Aspect ratio comes from slot config (default 1:1, admins can override).
 *   - If the slot is inactive or has no ads → nothing rendered (no empty space).
 */
const AdSlot = ({ slotCode, className }) => {
  const [state, setState] = useState({ slot: null, ads: [] });
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/ads/slot/${slotCode}`);
      setState(res.data || { slot: null, ads: [] });
    } catch { /* silent */ }
    finally { setLoaded(true); }
  }, [slotCode]);

  useEffect(() => { load(); }, [load]);

  if (!loaded) return null;
  const { slot, ads } = state;
  if (!slot || !ads || ads.length === 0) return null;

  const cols = Math.min(ads.length, 5);
  const gridStyle = { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` };
  const aspect = (slot.aspect_ratio || '1:1').replace(':', '/');

  const handleClick = (ad) => {
    api.post(`/ads/${ad.ad_id}/click`, { slot_id: slot.slot_id, placement: slot.placement }).catch(() => {});
    const url = ad.target_url || '#';
    if (ad.open_in_new_tab) window.open(url, '_blank', 'noopener,noreferrer');
    else if (url && url !== '#') window.location.href = url;
  };

  return (
    <section
      className={`w-full ${className || ''}`}
      data-testid={`ad-slot-${slot.slot_code}`}
      aria-label={`광고 · ${slot.slot_name}`}
    >
      <div className="grid gap-2 sm:gap-3" style={gridStyle}>
        {ads.slice(0, 5).map((ad) => (
          <button
            key={ad.ad_id}
            type="button"
            onClick={() => handleClick(ad)}
            className="group relative overflow-hidden rounded-xl border border-black/5 dark:border-white/10 bg-white dark:bg-[#141414] hover:border-[#FF3B30]/40 transition-colors text-left"
            data-testid={`ad-card-${ad.ad_id}`}
          >
            <div className="relative w-full overflow-hidden" style={{ aspectRatio: aspect }}>
              <span className="absolute top-1 left-1 z-10 text-[9px] font-black px-1 py-0.5 rounded bg-black/60 text-white tracking-wider">AD</span>
              <picture>
                {ad.mobile_image_url && <source media="(max-width: 640px)" srcSet={ad.mobile_image_url} />}
                <SafeImg
                  src={ad.image_url || ad.mobile_image_url}
                  kind="broadcast"
                  alt={ad.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300"
                />
              </picture>
              {ad.title && (
                <div className="absolute inset-x-0 bottom-0 p-1.5 sm:p-2 bg-gradient-to-t from-black/70 to-transparent">
                  <p
                    className="text-white text-[10px] sm:text-xs font-bold line-clamp-1"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {ad.title}
                  </p>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};

export default AdSlot;
